import { c as _c } from "react/compiler-runtime";
import { Fragment, useState } from 'react';
import styled, { css } from 'styled-components';
import { StyledCard, StyledCardSection } from '../base/Card';
import Icon from '../base/Icon';
import Link from '../base/Link';
import Skeleton from '../base/Skeleton';
import Text from '../base/Text';
import Tooltip from '../base/Tooltip';
import LegendColor from '../Chart/LegendColor';
import ScrollArea from '../helpers/ScrollArea';
import { Div, StyledUtilsProps } from '../helpers/StyledUtils';
import Loader from '../Loader';
import MouseTooltip from '../MouseTooltip';
import WithLoader from '../WithLoader';
import MetricError from './MetricError';
import MetricNoData from './MetricNoData';
export interface MetricTableRow {
  key: string;
  label: string;
  color?: string;
  value: number;
  display_value: string;
  extra_value?: string;
  tooltip?: {
    label: string;
    value: string;
  }[];
}
export interface MetricTableProps extends StyledUtilsProps {
  title: string;
  description?: string;
  rows: MetricTableRow[] | null;
  error?: boolean;
  header_right?: React.ReactNode;
  footer_text?: string;
  footer_link?: {
    label: string;
    to: string;
  };
  max_h?: {
    px: number;
  };
}
interface RowProps {
  row_key: string;
  hovered_key: string | null;
  label: string;
  display_value: string;
  extra_value?: string;
  color?: string;
  width: string;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}
const StyledRow = styled(Div)<{
  muted: boolean;
}>(({
  theme,
  muted
}) => css`
    position: relative;
    box-sizing: border-box;
    border-radius: ${theme.radius.small};
    overflow: hidden;
    opacity: ${muted ? 0.5 : 1};
    /* Contain the z-indexed label/value cells so they stack above the row
       background bar without escaping over page-level overlays. */
    isolation: isolate;
  `);
const StyledRowBackground = styled.div<{
  width: string;
  muted: boolean;
}>(({
  theme,
  width,
  muted
}) => css`
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    min-width: 4px;
    width: ${width};
    background-color: ${theme.colors.surface.base.variant_surface_2};
    border-radius: ${theme.radius.small};
    opacity: ${muted ? 0.5 : 1};
  `);
const Row = t0 => {
  const $ = _c(41);
  let color;
  let display_value;
  let extra_value;
  let hovered_key;
  let label;
  let other_props;
  let row_key;
  let width;
  if ($[0] !== t0) {
    ({
      row_key,
      hovered_key,
      label,
      display_value,
      extra_value,
      color,
      width,
      ...other_props
    } = t0);
    $[0] = t0;
    $[1] = color;
    $[2] = display_value;
    $[3] = extra_value;
    $[4] = hovered_key;
    $[5] = label;
    $[6] = other_props;
    $[7] = row_key;
    $[8] = width;
  } else {
    color = $[1];
    display_value = $[2];
    extra_value = $[3];
    hovered_key = $[4];
    label = $[5];
    other_props = $[6];
    row_key = $[7];
    width = $[8];
  }
  const hovered = hovered_key && hovered_key === row_key;
  const muted = hovered_key && !hovered;
  let t1;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      b: 3
    };
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  let t2;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      align: "center",
      justify: "space-between"
    };
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  const t3 = !!muted;
  const t4 = !!muted;
  let t5;
  if ($[11] !== t4 || $[12] !== width) {
    t5 = <StyledRowBackground width={width} muted={t4} />;
    $[11] = t4;
    $[12] = width;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  let t7;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      zIndex: 2
    };
    t7 = {
      align: "center",
      gap: 2
    };
    $[14] = t6;
    $[15] = t7;
  } else {
    t6 = $[14];
    t7 = $[15];
  }
  let t8;
  if ($[16] !== color || $[17] !== row_key) {
    t8 = <LegendColor id={row_key} color={color} />;
    $[16] = color;
    $[17] = row_key;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== label) {
    t9 = <Text mono={true}>{label}</Text>;
    $[19] = label;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== t8 || $[22] !== t9) {
    t10 = <Div style={t6} flex={t7}>{t8}{t9}</Div>;
    $[21] = t8;
    $[22] = t9;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  let t12;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = {
      zIndex: 2
    };
    t12 = {
      align: "center",
      gap: 3
    };
    $[24] = t11;
    $[25] = t12;
  } else {
    t11 = $[24];
    t12 = $[25];
  }
  let t13;
  if ($[26] !== display_value) {
    t13 = <Text>{display_value}</Text>;
    $[26] = display_value;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== extra_value) {
    t14 = extra_value && <Text muted={true} style={{
      minWidth: "48px",
      textAlign: "right"
    }}>{extra_value}</Text>;
    $[28] = extra_value;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] !== t13 || $[31] !== t14) {
    t15 = <Div style={t11} flex={t12}>{t13}{t14}</Div>;
    $[30] = t13;
    $[31] = t14;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] !== t10 || $[34] !== t15 || $[35] !== t3 || $[36] !== t5) {
    t16 = <StyledRow w={100} p={2} flex={t2} muted={t3}>{t5}{t10}{t15}</StyledRow>;
    $[33] = t10;
    $[34] = t15;
    $[35] = t3;
    $[36] = t5;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  let t17;
  if ($[38] !== other_props || $[39] !== t16) {
    t17 = <Div p={t1} {...other_props}>{t16}</Div>;
    $[38] = other_props;
    $[39] = t16;
    $[40] = t17;
  } else {
    t17 = $[40];
  }
  return t17;
};
const TableTooltip = t0 => {
  const $ = _c(5);
  const {
    tooltip_data
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      px: 296
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== tooltip_data) {
    t2 = tooltip_data.map(_temp);
    $[1] = tooltip_data;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t2) {
    t3 = <MouseTooltip><StyledCard raised={true} min_w={t1}>{t2}</StyledCard></MouseTooltip>;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
};
const StyledSkeletonLink = styled(Link)(({
  theme
}) => css`
    color: ${theme.colors.on.neutral.secondary_neutral};
    pointer-events: none;

    svg {
      fill: ${theme.colors.on.neutral.secondary_neutral};
    }
  `);
const MetricTableSkeleton = t0 => {
  const $ = _c(27);
  const {
    title,
    description,
    skeleton_count,
    total_count,
    collapsed,
    footer,
    scroll_position
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      x: 4,
      y: 4,
      b: 1
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      justify: "space-between",
      align: "center"
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      align: "center",
      gap: 1
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== title) {
    t4 = <Text semi={true} as="h3" m={0} ellipsis={true}>{title}</Text>;
    $[3] = title;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== description) {
    t5 = description && <Tooltip tooltip={description}><Icon muted={true} icon="info" /></Tooltip>;
    $[5] = description;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== t4 || $[8] !== t5) {
    t6 = <Div p={t1}><Div flex={t2}><Div flex={t3}>{t4}{t5}</Div></Div></Div>;
    $[7] = t4;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      px: 242
    };
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      direction: "column",
      gap: 3
    };
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== skeleton_count) {
    t9 = <Div flex={t8}>{Array.from(Array(skeleton_count)).map(_temp2)}</Div>;
    $[12] = skeleton_count;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== collapsed || $[15] !== total_count) {
    t10 = total_count > 1 && <Div m={{
      t: 3
    }}>{collapsed ? <StyledSkeletonLink neutral={true} onClick={_temp3} style={{
        pointerEvents: "none"
      }}>See {total_count - 1} more</StyledSkeletonLink> : <StyledSkeletonLink neutral={true} style={{
        pointerEvents: "none"
      }}>See less</StyledSkeletonLink>}</Div>;
    $[14] = collapsed;
    $[15] = total_count;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== scroll_position || $[18] !== t10 || $[19] !== t9) {
    t11 = <Div><ScrollArea p={4} max_h={t7} scroll_position={scroll_position}>{t9}{t10}</ScrollArea></Div>;
    $[17] = scroll_position;
    $[18] = t10;
    $[19] = t9;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== footer) {
    t12 = footer && <Div p={4}><Text muted={true}>{footer}</Text></Div>;
    $[21] = footer;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== t11 || $[24] !== t12 || $[25] !== t6) {
    t13 = <>{t6}{t11}{t12}</>;
    $[23] = t11;
    $[24] = t12;
    $[25] = t6;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  return t13;
};
const getWidth = (current: number, max: number) => {
  return `${current / max * 100}%`;
};
const MetricTable = t0 => {
  const $ = _c(55);
  let description;
  let error;
  let footer_link;
  let footer_text;
  let header_right;
  let max_h;
  let other_props;
  let rows;
  let title;
  if ($[0] !== t0) {
    ({
      title,
      description,
      rows,
      error,
      header_right,
      footer_text,
      footer_link,
      max_h,
      ...other_props
    } = t0);
    $[0] = t0;
    $[1] = description;
    $[2] = error;
    $[3] = footer_link;
    $[4] = footer_text;
    $[5] = header_right;
    $[6] = max_h;
    $[7] = other_props;
    $[8] = rows;
    $[9] = title;
  } else {
    description = $[1];
    error = $[2];
    footer_link = $[3];
    footer_text = $[4];
    header_right = $[5];
    max_h = $[6];
    other_props = $[7];
    rows = $[8];
    title = $[9];
  }
  let t1;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      skeleton_count: 3,
      total_count: 0,
      scroll_position: 0
    };
    $[10] = t1;
  } else {
    t1 = $[10];
  }
  const [loading_state, setLoadingState] = useState(t1);
  const [hovered_key, setHoveredKey] = useState(null);
  const loading = rows === null;
  const max = rows && rows.length > 0 ? rows[0].value : 0;
  let t2;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      direction: "column"
    };
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  const t3 = loading && !error;
  const t4 = footer_text && footer_link ? `${footer_text}${footer_link.label}` : footer_text ? footer_text : footer_link?.label ? footer_link.label : "";
  let t5;
  if ($[12] !== description || $[13] !== loading_state.scroll_position || $[14] !== loading_state.skeleton_count || $[15] !== loading_state.total_count || $[16] !== t4 || $[17] !== title) {
    t5 = <MetricTableSkeleton title={title} description={description} skeleton_count={loading_state.skeleton_count} total_count={loading_state.total_count} collapsed={false} footer={t4} scroll_position={loading_state.scroll_position} />;
    $[12] = description;
    $[13] = loading_state.scroll_position;
    $[14] = loading_state.skeleton_count;
    $[15] = loading_state.total_count;
    $[16] = t4;
    $[17] = title;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  const t6 = footer_text || footer_link ? 327 : 142;
  let t7;
  if ($[19] !== t6) {
    t7 = <Loader h={{
      px: t6
    }} message="Loading your data is taking a bit longer than usual. It should be ready in a few moments." />;
    $[19] = t6;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      x: 4,
      y: 4,
      b: 1
    };
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      justify: "space-between",
      align: "center"
    };
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      align: "center",
      gap: 1
    };
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== title) {
    t11 = <Text semi={true} as="h3" m={0} ellipsis={true}>{title}</Text>;
    $[24] = title;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== description) {
    t12 = description && <Tooltip tooltip={description}><Icon muted={true} icon="info" /></Tooltip>;
    $[26] = description;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== t11 || $[29] !== t12) {
    t13 = <Div flex={t10}>{t11}{t12}</Div>;
    $[28] = t11;
    $[29] = t12;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== header_right || $[32] !== t13) {
    t14 = <Div p={t8}><Div flex={t9}>{t13}{header_right}</Div></Div>;
    $[31] = header_right;
    $[32] = t13;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  let t15;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = {
      flex: 1,
      minHeight: 0
    };
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== error || $[36] !== hovered_key || $[37] !== loading_state.scroll_position || $[38] !== max || $[39] !== max_h || $[40] !== rows) {
    t16 = <Div style={t15}>{rows && rows.length > 0 ? <ScrollArea onScroll={e => setLoadingState(state => ({
        ...state,
        scroll_position: (e.target as HTMLElement).scrollTop || 0
      }))} scroll_position={loading_state.scroll_position} p={{
        t: 4,
        l: 4,
        r: 4,
        b: 2
      }} max_h={max_h || {
        px: 242
      }}><Div flex={{
          direction: "column"
        }}>{rows.map(row => <Fragment key={row.key}><Row row_key={row.key} hovered_key={hovered_key} label={row.label} display_value={row.display_value} extra_value={row.extra_value} color={row.color} width={getWidth(row.value, max)} onMouseEnter={() => setHoveredKey(row.key)} onMouseLeave={() => setHoveredKey(null)} />{hovered_key === row.key && row.tooltip && <TableTooltip tooltip_data={row.tooltip} />}</Fragment>)}</Div></ScrollArea> : <Div flex={{
        direction: "column",
        align: "center",
        justify: "center"
      }} h={max_h || {
        px: 242
      }}>{error ? <MetricError /> : <MetricNoData />}</Div>}</Div>;
    $[35] = error;
    $[36] = hovered_key;
    $[37] = loading_state.scroll_position;
    $[38] = max;
    $[39] = max_h;
    $[40] = rows;
    $[41] = t16;
  } else {
    t16 = $[41];
  }
  let t17;
  if ($[42] !== footer_link || $[43] !== footer_text) {
    t17 = (footer_text || footer_link) && <Div p={4}><Text>{footer_text && footer_text}{footer_link && <Link as="button" primary={true} to={footer_link.to}>{footer_link.label}</Link>}</Text></Div>;
    $[42] = footer_link;
    $[43] = footer_text;
    $[44] = t17;
  } else {
    t17 = $[44];
  }
  let t18;
  if ($[45] !== t14 || $[46] !== t16 || $[47] !== t17 || $[48] !== t3 || $[49] !== t5 || $[50] !== t7) {
    t18 = <WithLoader loading={t3} loading_element={t5} long_loading_element={t7}>{t14}{t16}{t17}</WithLoader>;
    $[45] = t14;
    $[46] = t16;
    $[47] = t17;
    $[48] = t3;
    $[49] = t5;
    $[50] = t7;
    $[51] = t18;
  } else {
    t18 = $[51];
  }
  let t19;
  if ($[52] !== other_props || $[53] !== t18) {
    t19 = <StyledCard flex={t2} {...other_props}>{t18}</StyledCard>;
    $[52] = other_props;
    $[53] = t18;
    $[54] = t19;
  } else {
    t19 = $[54];
  }
  return t19;
};
export default MetricTable;
function _temp(t0) {
  const {
    label,
    value
  } = t0;
  return <StyledCardSection p={3} key={label}><Div flex={{
      align: "center",
      justify: "space-between"
    }}><Text muted={true} m={{
        r: 2
      }}>{label}</Text><Text muted={true}>{value}</Text></Div></StyledCardSection>;
}
function _temp2(_, i) {
  return <Skeleton key={i} h={{
    px: 36
  }} w={{
    raw: `${i < 3 ? 100 - i * 10 : 70}%`
  }} />;
}
function _temp3() {
  return null;
}